import { ActionsObservable, StateObservable } from 'redux-observable';
import { from, queueScheduler, Subject } from 'rxjs';
import { map, mergeMap, observeOn, subscribeOn } from 'rxjs/operators';
import { tryToFilterBeforeScheduling } from './tryToFilterBeforeScheduling';

/**
 * Copy of the official createEpicMiddleware from redux-observable.
 *
 * There is one change - before scheduling an action, we try to filter it out if possible.
 * Otherwise every action would be scheduled separately for each epic, which causes huge performance issues,
 * when dispatching many actions at once.
 *
 * @param {} options
 * @returns
 */
export function createOptimizedEpicMiddleware(options = {}) {
  // This isn't great. RxJS doesn't publicly export the constructor for
  // QueueScheduler nor QueueAction, so we reach in. We need to do this because
  // we don't want our internal queuing mechanism to be on the same queue as any
  // other RxJS code outside of redux-observable internals.
  const QueueScheduler = queueScheduler.constructor;
  const uniqueQueueScheduler = new QueueScheduler(
    queueScheduler.SchedulerAction
  );

  if (process.env.NODE_ENV !== 'production' && typeof options === 'function') {
    throw new TypeError(
      'Providing your root Epic to `createEpicMiddleware(rootEpic)` is no longer supported, instead use `epicMiddleware.run(rootEpic)`\n\nLearn more: https://redux-observable.js.org/MIGRATION.html#setting-up-the-middleware'
    );
  }

  const epic$ = new Subject();
  let store;

  const epicMiddleware = _store => {
    // if (process.env.NODE_ENV !== 'production' && store) {
    //   // https://github.com/redux-observable/redux-observable/issues/389
    //   warn(
    //     'this middleware is already associated with a store. createEpicMiddleware should be called for every store.\n\nLearn more: https://goo.gl/2GQ7Da'
    //   );
    // }
    store = _store;
    const actionSubject$ = new Subject();
    const stateSubject$ = new Subject().pipe(observeOn(uniqueQueueScheduler));
    const action$ = new ActionsObservable(
      actionSubject$.pipe(
        tryToFilterBeforeScheduling(),
        observeOn(uniqueQueueScheduler)
      )
    );
    const state$ = new StateObservable(stateSubject$, store.getState());

    const result$ = epic$.pipe(
      map(epic => {
        const output$ =
          'dependencies' in options
            ? epic(action$, state$, options.dependencies)
            : epic(action$, state$);

        if (!output$) {
          throw new TypeError(
            `Your root Epic "${
              epic.name || '<anonymous>'
            }" does not return a stream. Double check you're not missing a return statement!`
          );
        }

        return output$;
      }),
      mergeMap(output$ =>
        from(output$).pipe(
          subscribeOn(uniqueQueueScheduler),
          observeOn(uniqueQueueScheduler)
        )
      )
    );

    result$.subscribe(store.dispatch);

    return next => {
      return action => {
        // Downstream middleware gets the action first,
        // which includes their reducers, so state is
        // updated before epics receive the action
        const result = next(action);

        // It's important to update the state$ before we emit
        // the action because otherwise it would be stale
        stateSubject$.next(store.getState());
        actionSubject$.next(action);

        return result;
      };
    };
  };

  epicMiddleware.run = rootEpic => {
    // if (process.env.NODE_ENV !== 'production' && !store) {
    //   warn(
    //     'epicMiddleware.run(rootEpic) called before the middleware has been setup by redux. Provide the epicMiddleware instance to createStore() first.'
    //   );
    // }
    epic$.next(rootEpic);
  };

  return epicMiddleware;
}
